// src/firebase/users.ts
//
// Shared, cached access to the `users` collection.
// Replaces three duplicated inline queries in:
//   - components/ManualRegistrationModal.tsx  (coaches dropdown)
//   - pages/admin/ManageCreditPackages.tsx    (coaches dropdown)
//   - pages/admin/AdminRecentRegistrations.tsx (coachId → name/email map)
//   - pages/admin/AdminCoaches.tsx            (full users table)

import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "./config";
import { withCache, invalidateCachePrefix } from "../utils/cache";

export interface UserDoc {
  uid: string;
  email: string;
  displayName: string;
  role?: "admin" | "coach" | "ranking_admin";
  club?: string;
  phone?: string;
  createdAt?: any;
}

// 5 min TTL — users rarely change; admin writes invalidate immediately.
const TTL_USERS = 5 * 60 * 1000;

/** All users (any role). Used by AdminCoaches. */
export const getAllUsers = (): Promise<UserDoc[]> =>
  withCache("users:all", TTL_USERS, async () => {
    const snap = await getDocs(query(collection(db, "users")));
    return snap.docs.map((d) => ({ uid: d.id, ...d.data() } as UserDoc));
  });

/** Only role === "coach", ordered by displayName. */
export const getCoachUsers = (): Promise<UserDoc[]> =>
  withCache("users:coaches", TTL_USERS, async () => {
    const snap = await getDocs(
      query(
        collection(db, "users"),
        where("role", "==", "coach"),
        orderBy("displayName")
      )
    );
    return snap.docs.map((d) => ({ uid: d.id, ...d.data() } as UserDoc));
  });

/** Call after any users-collection write (create / edit / delete). */
export const invalidateUsersCache = (): void => {
  invalidateCachePrefix("users:");
};
